import {
  UserNotificationSourceKind,
  UserNotificationStatus,
} from '@automatick/shared-ts';
import { type UserNotificationResponse } from '@automatick/shared-ng';
import { BehaviorSubject } from 'rxjs';

const BROWSER_CARD_LIMIT = 3;
const BROWSER_CARD_AUTO_DISMISS_MS = 9000;

export class UserNotificationBrowserCardStore {
  private readonly cardsSubject = new BehaviorSubject<
    readonly UserNotificationResponse[]
  >([]);
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private activeConversationSessionId: string | null = null;

  readonly cards$ = this.cardsSubject.asObservable();

  constructor(private readonly isDocumentVisible: () => boolean) {}

  setActiveConversationSession(sessionId: string | null | undefined): void {
    this.activeConversationSessionId = sessionId?.trim() || null;
    if (!this.activeConversationSessionId) {
      return;
    }

    this.setCards(
      this.cardsSubject.value.filter(
        (notification) => !this.isCurrentVisibleConversation(notification),
      ),
    );
  }

  show(notification: UserNotificationResponse): void {
    const notificationId = notification.notificationId;
    if (
      !notificationId ||
      notification.status === UserNotificationStatus.Dismissed ||
      this.isCurrentVisibleConversation(notification)
    ) {
      return;
    }

    this.clearTimer(notificationId);
    const existing = this.cardsSubject.value.filter(
      (item) => item.notificationId !== notificationId,
    );
    this.setCards([notification, ...existing].slice(0, BROWSER_CARD_LIMIT));

    const needsAction =
      notification.requiresAction === true ||
      notification.status === UserNotificationStatus.ActionRequired;
    if (!needsAction) {
      this.timers.set(
        notificationId,
        setTimeout(
          () => this.dismiss(notificationId),
          BROWSER_CARD_AUTO_DISMISS_MS,
        ),
      );
    }
  }

  dismiss(notificationId: string | null | undefined): void {
    const normalizedId = notificationId?.trim();
    if (!normalizedId) {
      return;
    }

    this.clearTimer(normalizedId);
    this.setCards(
      this.cardsSubject.value.filter(
        (notification) => notification.notificationId !== normalizedId,
      ),
    );
  }

  clear(): void {
    for (const notificationId of this.timers.keys()) {
      this.clearTimer(notificationId);
    }
    this.cardsSubject.next([]);
  }

  private isCurrentVisibleConversation(
    notification: UserNotificationResponse,
  ): boolean {
    return (
      this.isDocumentVisible() &&
      notification.sourceKind === UserNotificationSourceKind.Conversation &&
      !!notification.sourceId &&
      notification.sourceId === this.activeConversationSessionId
    );
  }

  private setCards(cards: readonly UserNotificationResponse[]): void {
    const visibleIds = new Set(
      cards.map((item) => item.notificationId).filter(Boolean),
    );
    for (const notificationId of this.timers.keys()) {
      if (!visibleIds.has(notificationId)) {
        this.clearTimer(notificationId);
      }
    }
    this.cardsSubject.next(cards);
  }

  private clearTimer(notificationId: string): void {
    const timer = this.timers.get(notificationId);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(notificationId);
    }
  }
}
